import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../../auth/useAuth';
import { useModerator } from '../../hooks/useModerator';
import { useMessages } from '../../hooks/useMessages';
import { Button } from '../common/Button';
import { LanguageSelector } from '../common/LanguageSelector';
import { ThemeToggle } from '../common/ThemeToggle';
import { UserAvatar } from '../common/UserAvatar';
import { BrandMark } from '../common/BrandMark';
import { NotificationBell } from '../notification';
import { useLanguage } from '../../i18n/useLanguage';
import type { LanguageContextValue } from '../../i18n/LanguageProvider';
import { useProfilePicture } from '../../context/ProfilePictureContext';
import { hasAnyRole } from '../../auth/roleUtils';
import { getUnreadCount as getNotificationUnreadCount } from '../../api/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';

const NOTIFICATION_UNREAD_POLL_MS = 30_000;

type TranslationKey = Parameters<LanguageContextValue['t']>[0];

/** Static / help destinations at the bottom of the slide-out menu. */
const HEADER_INFO_MENU_GROUPS: { to: string; labelKey: TranslationKey }[][] = [
  [
    { to: '/missed-connections', labelKey: 'static.missedConnections' },
    { to: '/about', labelKey: 'static.about' },
  ],
  [
    { to: '/symbol', labelKey: 'static.symbol' },
    { to: '/show-symbol', labelKey: 'static.showSymbol' },
    { to: '/signature-song', labelKey: 'static.signatureSong' },
  ],
  [
    { to: '/contact', labelKey: 'static.contact' },
    { to: '/faq', labelKey: 'static.faq' },
    { to: '/help', labelKey: 'static.help' },
  ],
  [
    { to: '/community-rules', labelKey: 'static.communityRules' },
    { to: '/terms', labelKey: 'static.terms' },
    { to: '/privacy', labelKey: 'static.privacy' },
    { to: '/imprint', labelKey: 'static.imprint' },
  ],
];

export const Header: React.FC = () => {
  const { isAuthenticated, user, login, logout, loading } = useAuth();
  const { t } = useLanguage();
  const { avatarVersion } = useProfilePicture();
  const isModerator = useModerator();
  const { unreadCount } = useMessages();
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuNavRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const refreshNotificationUnreadOnly = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await getNotificationUnreadCount();
      setNotificationUnreadCount(count);
    } catch {
      /* network / backend down — same as notification hook */
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotificationUnreadCount(0);
      return;
    }
    void refreshNotificationUnreadOnly();
    const id = window.setInterval(() => {
      void refreshNotificationUnreadOnly();
    }, NOTIFICATION_UNREAD_POLL_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated, refreshNotificationUnreadOnly]);

  const prevMenuOpenRef = useRef(false);
  useEffect(() => {
    if (prevMenuOpenRef.current && !menuOpen && isAuthenticated) {
      void refreshNotificationUnreadOnly();
    }
    prevMenuOpenRef.current = menuOpen;
  }, [menuOpen, isAuthenticated, refreshNotificationUnreadOnly]);

  useEffect(() => {
    if (!menuOpen) return;
    const id = requestAnimationFrame(() => {
      const panel = menuNavRef.current;
      const focusable = panel?.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [menuOpen]);

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      // Login failed
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      // Logout failed
    }
  };

  const menuToggleHasUnread =
    isAuthenticated &&
    !menuOpen &&
    (unreadCount > 0 || notificationUnreadCount > 0);

  return (
    <header className={clsx('header', 'header--site-menu-drawer', menuOpen && 'header--menu-open')}>
      <div className="header-container">
        <Link to="/" className="header-logo">
          <BrandMark variant="header" />
        </Link>

        <div className="header-brand-group">
          <span className="header-site-title">I Think It's You</span>
        </div>

        <button
          type="button"
          className={clsx(
            'header-menu-toggle',
            menuToggleHasUnread && 'header-menu-toggle--has-unread',
          )}
          aria-expanded={menuOpen}
          aria-controls="header-site-menu"
          onClick={() => {
            setMenuOpen((o) => !o);
          }}
          aria-label={
            menuOpen
              ? t('header.closeMainMenu')
              : menuToggleHasUnread
                ? t('header.openMainMenuWithUnread')
                : t('header.openMainMenu')
          }
          title={
            menuOpen ? undefined : menuToggleHasUnread ? t('header.openMainMenuWithUnread') : t('header.openMainMenu')
          }
        >
          {menuOpen ? (
            <svg
              className="header-menu-toggle-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden={true}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg
              className="header-menu-toggle-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden={true}
            >
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>

        <div
          className="header-nav-backdrop"
          aria-hidden={true}
          onClick={() => setMenuOpen(false)}
        />

        <nav
          ref={menuNavRef}
          id="header-site-menu"
          className="header-auth"
          aria-label={t('header.mainNavigation')}
          inert={!menuOpen ? true : undefined}
        >
          <LanguageSelector />
          <ThemeToggle />
          {loading ? (
            <span>{t('common.loading')}</span>
          ) : isAuthenticated ? (
            <div className="header-user">
              {hasAnyRole(user?.roles, 'ADMIN') && (
                <>
                  <Link to="/administration/users" className="header-nav-link">
                    {t('header.users')}
                  </Link>
                  <Link to="/administration/moderators" className="header-nav-link">
                    {t('header.moderators')}
                  </Link>
                  <Link to="/administration/statistics" className="header-nav-link">
                    {t('header.statistics')}
                  </Link>
                </>
              )}
              {isModerator && (
                <>
                  <Link to="/moderation/flagged-posts" className="header-nav-link">
                    {t('header.flaggedPosts')}
                  </Link>
                  <Link to="/moderation/flagged-comments" className="header-nav-link">
                    {t('header.flaggedComments')}
                  </Link>
                  <Link to="/moderation/deleted-posts" className="header-nav-link">
                    {t('header.deletedPosts')}
                  </Link>
                  <Link to="/moderation/deleted-comments" className="header-nav-link">
                    {t('header.deletedComments')}
                  </Link>
                </>
              )}
              <NotificationBell />
              <div className="header-messages-container">
                <Link
                  to="/messages"
                  className="header-messages-link"
                  aria-label={`${t('header.messages')}${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                  title={`${t('header.messages')}${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                >
                  <FontAwesomeIcon icon={faComments} className="header-messages-icon" aria-hidden />
                  {unreadCount > 0 && (
                    <span className="header-messages-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                  <span className="header-menu-item-label">{t('header.messages')}</span>
                </Link>
              </div>
              <Link
                to="/profile"
                className="header-user-link"
                aria-label={`${t('header.profile')} (${user?.preferred_username || user?.email || 'User'})`}
                title={`${t('header.profile')} - ${user?.preferred_username || user?.email || 'User'}`}
              >
                <UserAvatar
                  userId={user!.sub}
                  username={user?.preferred_username || user?.email}
                  size={28}
                  className="header-user-avatar"
                  cacheKey={avatarVersion || undefined}
                />
                <span className="header-menu-item-label">{t('header.profile')}</span>
              </Link>
              <Button
                type="button"
                variant="outline"
                size="small"
                className="header-auth-action-button"
                onClick={handleLogout}
                aria-label={t('common.logout')}
                title={t('common.logout')}
              >
                <svg
                  className="header-auth-action-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden={true}
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="header-auth-action-label">{t('common.logout')}</span>
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="small"
              className="header-auth-action-button"
              onClick={handleLogin}
              aria-label={t('common.login')}
              title={t('common.login')}
            >
              <svg
                className="header-auth-action-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden={true}
              >
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              <span className="header-auth-action-label">{t('common.login')}</span>
            </Button>
          )}
          <div className="header-mobile-drawer-section" aria-label={t('header.infoMenu')}>
            <p className="header-mobile-drawer-section-label">{t('header.infoMenu')}</p>
            {HEADER_INFO_MENU_GROUPS.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {groupIndex > 0 && <div className="header-mobile-drawer-divider" />}
                {group.map(({ to, labelKey }) => (
                  <Link key={to} to={to} className="header-mobile-drawer-link">
                    {t(labelKey)}
                  </Link>
                ))}
              </React.Fragment>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};
